import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ProjectSubmit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", organization: "", phone: "",
    project_title: "", project_summary: "", support_requested: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/projects/submit", form);
      setSent(true);
      toast.success("Thanks — we'll be in touch about your project.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="project-submit-success">
        <CheckCircle2 size={48} className="mx-auto text-[color:var(--azsam-sage-hover)]" strokeWidth={1.5} />
        <div className="overline mt-6">Received</div>
        <h1 className="mt-4 font-heading text-4xl">Thank you.</h1>
        <p className="mt-4 text-[color:var(--azsam-ink-soft)]">
          Your project submission has been received. A member of our board will review it and reach
          out about how AzSAM can help amplify your work.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/news" className="btn-outline" data-testid="project-submit-back-news">← Back to Initiatives</Link>
          <button onClick={() => { setSent(false); setForm({ name:"",email:"",organization:"",phone:"",project_title:"",project_summary:"",support_requested:"" }); }} className="btn-primary" data-testid="project-submit-another">Submit another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="project-submit-page">
      <Link to="/news" className="inline-flex items-center gap-2 text-sm text-[color:var(--azsam-ink-soft)] hover:text-[color:var(--azsam-clay)]" data-testid="project-submit-back">
        <ArrowLeft size={16} /> Back to Initiatives
      </Link>
      <div className="mt-8">
        <div className="overline">Member project spotlight</div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl leading-tight">Tell us about your project</h1>
        <p className="mt-5 text-[color:var(--azsam-ink-soft)] leading-relaxed">
          Share the work you're leading — advocacy, research, education, community outreach, policy,
          overdose prevention, recovery initiatives, or innovative clinical programs. AzSAM may be
          able to help amplify your efforts, connect you with collaborators, provide subject-matter
          expertise, or champion your initiative across the finish line.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-10 azsam-card p-8 space-y-5" data-testid="project-submit-form">
        <div className="grid md:grid-cols-2 gap-5">
          <F label="Your full name" required>
            <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className={input} data-testid="project-submit-name" placeholder="Dr. Casey Rivera" />
          </F>
          <F label="Email" required>
            <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className={input} data-testid="project-submit-email" placeholder="you@clinic.org" />
          </F>
          <F label="Organization (optional)">
            <input value={form.organization} onChange={(e)=>setForm({...form,organization:e.target.value})} className={input} data-testid="project-submit-org" />
          </F>
          <F label="Phone (optional)">
            <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className={input} data-testid="project-submit-phone" />
          </F>
        </div>

        <F label="Project title" required>
          <input required value={form.project_title} onChange={(e)=>setForm({...form,project_title:e.target.value})} className={input} data-testid="project-submit-title" placeholder="e.g., Rural naloxone distribution pilot in Yavapai County" />
        </F>

        <F label="Short summary of your project" required>
          <textarea required rows={5} value={form.project_summary} onChange={(e)=>setForm({...form,project_summary:e.target.value})} className={input + " resize-y"} data-testid="project-submit-summary" placeholder="What is the project, who does it serve, and where does it operate?" />
        </F>

        <F label="What would you like from AzSAM?" required>
          <textarea required rows={4} value={form.support_requested} onChange={(e)=>setForm({...form,support_requested:e.target.value})} className={input + " resize-y"} data-testid="project-submit-support" placeholder="e.g., amplification to our network, collaborators, subject-matter expert reviewers, letter of support, presentation opportunity…" />
        </F>

        <div className="flex justify-end">
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="project-submit-btn">
            {submitting ? "Submitting…" : (<>Submit project <ArrowRight size={16} /></>)}
          </button>
        </div>
      </form>
    </div>
  );
}

const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition";

function F({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-2 font-semibold">
        {label}{required && <span className="text-[color:var(--azsam-clay)]"> *</span>}
      </div>
      {children}
    </label>
  );
}
